/**
 * Shared app-search engine (integrations-page UX overhaul, 2026-08-18).
 *
 * ONE scored matcher for every surface that resolves a typed app name against
 * the Composio catalog: the Integrations page omnisearch, the "connect any
 * system" lane, and the in-chat connect-app resolver. Promotes the chat
 * resolver's proven ladder (exact → keyword → contains) and extends it with
 * prefix and bounded-edit-distance tiers so "gmial", "outlok", and "email"
 * all land on the right tiles instead of falling through to a custom
 * registration.
 *
 * Scoring tiers (lower = better):
 *   0 exact     — slug/name equality (raw, whitespace-collapsed, underscored)
 *   1 synonym   — word-boundary hit in SEARCH_SYNONYMS ("email" → gmail)
 *   2 prefix    — a name/slug word starts with the query ("outl" → Outlook)
 *   3 substring — bidirectional contains (the old fuzzy tier)
 *   4 fuzzy     — Levenshtein ≤ 1 (short names) / ≤ 2 (longer), typo rescue
 *   5 related   — `broad` only: every query word is a word of the toolkit's
 *                 description, categories or family
 *
 * The Integrations page searches `broad` (2026-10-08, Miles: everything the
 * page lists shows up in search, "even if the search doesn't perfectly
 * match"): the name's words also match in any order, each with its own typo
 * rescue, and the related tier finds an app by what it does. The legal
 * catalog's search (panel/legalAppsSearch.ts) ranks on this same ladder with
 * the helpers below, so the page can merge both answers by tier.
 */

import { familyForToolkit, sortByLikelihood } from './ComposioClient';
import { connectLaneFor, laneIsDirect } from './connectLane';
import type { ComposioToolkit } from './ComposioClient';
import { editDistanceWithin } from '../search/editDistance';

export type MatchReason = 'exact' | 'synonym' | 'prefix' | 'substring' | 'fuzzy' | 'related';

export interface ScoredToolkit {
  tk: ComposioToolkit;
  /** Lower = better. Equals the tier that produced the match. */
  score: number;
  reason: MatchReason;
}

const REASON_BY_TIER: MatchReason[] = ['exact', 'synonym', 'prefix', 'substring', 'fuzzy', 'related'];

// ============================================================================
// Synonyms
// ============================================================================

/**
 * Keyword/synonym → toolkit slug map. Keys are REAL catalog slugs (verified
 * against the curated lists — never invent a slug). Superset of the old
 * connect-app APP_KEYWORD_MAP: same entries plus the everyday vocabulary
 * organizations actually type ("email", "e-sign", "microsoft 365", …).
 * Matching is word-boundary, never bare substring — 'box' must not fire on
 * "inbox"/"dropbox" (review finding, 2026-08-11).
 */
export const SEARCH_SYNONYMS: Record<string, string[]> = {
  gmail: ['gmail', 'google mail', 'email', 'e-mail', 'mail', 'correo'],
  outlook: ['outlook', 'email', 'e-mail', 'mail', 'microsoft mail', 'microsoft 365', 'office 365', 'hotmail'],
  googlecalendar: ['google calendar', 'calendar', 'calendario', 'meeting', 'appointment', 'scheduling'],
  calendly: ['calendly', 'scheduling link', 'booking'],
  googledrive: ['google drive', 'drive', 'files', 'storage', 'cloud storage'],
  googledocs: ['google docs', 'google doc', 'docs', 'documents'],
  googlesheets: ['google sheets', 'sheets', 'spreadsheet', 'spreadsheets', 'excel'],
  googlecontacts: ['google contacts', 'contacts'],
  slack: ['slack'],
  microsoft_teams: ['teams', 'microsoft teams', 'ms teams', 'microsoft 365', 'office 365'],
  one_drive: ['onedrive', 'one drive', 'microsoft 365', 'office 365', 'files', 'storage'],
  dropbox: ['dropbox', 'files', 'storage'],
  box: ['box', 'files', 'storage'],
  notion: ['notion', 'wiki', 'notes'],
  airtable: ['airtable'],
  hubspot: ['hubspot', 'crm'],
  salesforce: ['salesforce', 'crm'],
  zendesk: ['zendesk', 'crm', 'helpdesk', 'support tickets'],
  github: ['github', 'git'],
  gitlab: ['gitlab', 'git'],
  linear: ['linear'],
  jira: ['jira'],
  asana: ['asana'],
  trello: ['trello'],
  monday: ['monday'],
  clickup: ['clickup'],
  todoist: ['todoist'],
  basecamp: ['basecamp'],
  zoom: ['zoom', 'video call', 'video conferencing'],
  whatsapp: ['whatsapp', 'whats app'],
  intercom: ['intercom'],
  stripe: ['stripe', 'payments'],
  quickbooks: ['quickbooks', 'quick books', 'bookkeeping', 'accounting'],
  freshbooks: ['freshbooks', 'accounting'],
  mailchimp: ['mailchimp', 'newsletter'],
  linkedin: ['linkedin', 'linked in'],
  youtube: ['youtube', 'you tube', 'yt', 'video', 'videos', 'channel', 'vlog', 'shorts'],
  typeform: ['typeform', 'forms', 'survey', 'surveys'],
  docusign: ['docusign', 'e-sign', 'esign', 'esignature', 'e-signature', 'signature', 'signatures', 'sign documents'],
  miro: ['miro', 'whiteboard'],
  figma: ['figma'],
  canva: ['canva'],
};

/**
 * Back-compat alias: connect-app's intent map moved here (2026-08-18). Same
 * object — one source of truth for "what the user might call this app".
 */
export const APP_KEYWORD_MAP = SEARCH_SYNONYMS;

// ============================================================================
// First actions (the wow moment)
// ============================================================================

/**
 * Per-app suggested FIRST action, offered right after a connect completes —
 * one click opens chat with this prompt seeded. Written as the user's own
 * words. Fallback for unmapped apps: firstActionForApp composes a generic
 * "what can you do with <app>?".
 */
export const FIRST_ACTION_MAP: Record<string, string> = {
  gmail: 'summarize my unread email',
  outlook: 'summarize my unread email',
  googlecalendar: "what's on my calendar this week?",
  calendly: 'what bookings do I have coming up?',
  googledrive: 'what are my most recent documents?',
  googledocs: 'list my most recent documents',
  googlesheets: 'list my most recent spreadsheets',
  googlecontacts: 'look up a contact for me',
  slack: "summarize what I've missed in Slack",
  microsoft_teams: "summarize what I've missed in Teams",
  one_drive: 'what are my most recent files?',
  dropbox: 'what are my most recent files?',
  box: 'what are my most recent files?',
  notion: 'what pages did we update recently?',
  hubspot: 'summarize my open deals',
  salesforce: 'summarize my open opportunities',
  zendesk: 'summarize my open tickets',
  stripe: 'how did payments look this week?',
  quickbooks: 'summarize unpaid invoices',
  docusign: 'which envelopes are still waiting for signature?',
  github: 'what pull requests need my attention?',
  jira: "what's assigned to me right now?",
  linear: "what's assigned to me right now?",
  asana: "what's assigned to me right now?",
  trello: "what's on my board?",
  whatsapp: 'summarize my recent WhatsApp messages',
  zoom: 'what meetings do I have coming up?',
  mailchimp: 'how did my last campaign perform?',
  typeform: 'summarize my latest form responses',
  youtube: 'list the videos on my channel',
};

/** The one-click "try it now" prompt for a just-connected app. */
export function firstActionForApp(slug: string, displayName?: string): string {
  const mapped = FIRST_ACTION_MAP[String(slug || '').toLowerCase()];
  if (mapped) return mapped;
  const name = (displayName || slug || 'this app').trim();
  return `what can you do with ${name}?`;
}

// ============================================================================
// Edit distance (typo rescue)
// ============================================================================

// The bounded distance lives in services/search/editDistance.ts since
// 2026-09-09 (the thread search rescues typos the same way); imported at the
// top of this file and re-exported here so every existing importer keeps its path.
export { editDistanceWithin };

// ============================================================================
// The matcher
// ============================================================================

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Word-boundary test that tolerates multi-word keywords ("google drive"). */
function wordBoundaryHit(haystack: string, keyword: string): boolean {
  if (haystack === keyword) return true;
  const re = new RegExp(`(^|[^a-z0-9])${escapeRegExp(keyword)}($|[^a-z0-9])`, 'i');
  return re.test(haystack);
}

/** The words a search compares: accents folded, lowercase, every run of other characters one space. */
export function foldSearchWords(text: unknown): string {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** The words of a text, folded: what wordsMatchTier and relatedHit compare (a search folds its query once). */
export const searchWords = (text: unknown): string[] => foldSearchWords(text).split(' ').filter(Boolean);

/** A query or a name as its words: already-split words pass through. */
const asWords = (text: string | readonly string[]): readonly string[] => (typeof text === 'string' ? searchWords(text) : text);

/**
 * A word within the typo budget of another, or of its first letters (1 edit up to 6 letters, else 2). A typo keeps
 * the first letter ("lexpy", "imigration", "gogle"): without that, "gmail" and "email" were typos of "mail".
 */
function typoOf(word: string, other: string): boolean {
  if (word[0] !== other[0]) return false;
  const max = word.length <= 6 ? 1 : 2;
  return editDistanceWithin(word, other, max)
    || (other.length > word.length && editDistanceWithin(word, other.slice(0, word.length), max));
}

/**
 * A query's words against an app's names, in any order: every query word
 * starts a word of ONE name, or (3+ letters) sits inside one, or (4+ letters)
 * is a typo of one. 3 when every word was found as typed, 4 when any needed
 * the typo rescue, null when a word is missing. Each name is checked on its
 * own, so two names never combine into a match. The query and the names may
 * come already split by searchWords.
 */
export function wordsMatchTier(
  query: string | readonly string[],
  names: ReadonlyArray<string | readonly string[]>,
): 3 | 4 | null {
  const words = asWords(query);
  if (words.length === 0) return null;
  let best: 3 | 4 | null = null;
  for (const name of names) {
    const nameWords = asWords(name);
    if (nameWords.length === 0) continue;
    let tier: 3 | 4 = 3;
    const every = words.every((w) => {
      if (nameWords.some((n) => n.startsWith(w) || (w.length >= 3 && n.includes(w)))) return true;
      if (w.length >= 4 && nameWords.some((n) => typoOf(w, n))) {
        tier = 4;
        return true;
      }
      return false;
    });
    if (every && (best === null || tier < best)) best = tier;
    if (best === 3) break;
  }
  return best;
}

/**
 * The related tier's test: every query word starts a word of a text already
 * folded by foldSearchWords. With `typos`, a word of 5+ letters may also be a
 * typo of one ("imigration"). The query may come already split by searchWords.
 */
export function relatedHit(query: string | readonly string[], foldedText: string, opts?: { typos?: boolean }): boolean {
  const words = asWords(query);
  if (words.length === 0 || !foldedText) return false;
  const padded = ` ${foldedText}`;
  let textWords: string[] | null = null;
  return words.every((w) => {
    if (padded.includes(` ${w}`)) return true;
    if (!opts?.typos || w.length < 5) return false;
    textWords = textWords || foldedText.split(' ');
    return textWords.some((t) => typoOf(w, t));
  });
}

/** A toolkit's name and slug as words, split once per toolkit object (a keystroke re-splits nothing). */
const nameWordsCache = new WeakMap<ComposioToolkit, string[][]>();
function nameWordsOf(tk: ComposioToolkit): string[][] {
  let words = nameWordsCache.get(tk);
  if (words === undefined) {
    words = [searchWords(tk.name || ''), searchWords((tk.slug || '').replace(/_/g, ' '))];
    nameWordsCache.set(tk, words);
  }
  return words;
}

/** A toolkit's related words (description, categories, family), folded once per toolkit object. */
const relatedTextCache = new WeakMap<ComposioToolkit, string>();
function relatedTextOf(tk: ComposioToolkit): string {
  let text = relatedTextCache.get(tk);
  if (text === undefined) {
    text = foldSearchWords([
      tk.meta?.description || '',
      ...(tk.meta?.categories || []).map((c) => c?.name || ''),
      familyForToolkit(tk),
    ].join(' '));
    relatedTextCache.set(tk, text);
  }
  return text;
}

export interface SearchToolkitsOptions {
  /** Cap the returned list (applied after ranking). Default: unlimited. */
  limit?: number;
  /**
   * The Integrations page's thorough search: the name's words in any order
   * (tier 3, or 4 through a typo) and the related tier (5). Off for the chat
   * resolver and onboarding, whose cards keep the narrower ladder.
   */
  broad?: boolean;
}

/**
 * Rank the catalog against a typed query. Returns every match, best first:
 * score (tier) → genuinely-one-click first → curated likelihood → tool count
 * → name. Empty/too-short queries return [].
 */
export function searchToolkits(
  query: string,
  toolkits: ComposioToolkit[],
  opts?: SearchToolkitsOptions
): ScoredToolkit[] {
  const normalized = (query || '').trim().toLowerCase();
  if (!normalized || toolkits.length === 0) return [];

  const collapsed = normalized.replace(/\s+/g, '');
  const underscored = normalized.replace(/\s+/g, '_');

  const bySlug = new Map<string, ComposioToolkit>();
  for (const tk of toolkits) {
    if (tk?.slug) bySlug.set(tk.slug.toLowerCase(), tk);
  }

  const scored = new Map<string, ScoredToolkit>();
  const add = (tk: ComposioToolkit | undefined, tier: number) => {
    if (!tk?.slug) return;
    const key = tk.slug.toLowerCase();
    const existing = scored.get(key);
    if (!existing || tier < existing.score) {
      scored.set(key, { tk, score: tier, reason: REASON_BY_TIER[tier] });
    }
  };

  // Tier 0 — exact slug / name.
  add(bySlug.get(normalized), 0);
  add(bySlug.get(collapsed), 0);
  add(bySlug.get(underscored), 0);
  for (const tk of toolkits) {
    const name = (tk.name || '').toLowerCase();
    if (name && (name === normalized || name === collapsed)) add(tk, 0);
  }

  // Tier 1 — synonym / intent keyword (word-boundary, never bare substring).
  for (const [slug, keywords] of Object.entries(SEARCH_SYNONYMS)) {
    const tk = bySlug.get(slug);
    if (!tk) continue;
    for (const keyword of keywords) {
      if (wordBoundaryHit(normalized, keyword)) {
        add(tk, 1);
        break;
      }
    }
  }

  // Tier 2 — prefix: a word of the name (or the slug) starts with the query.
  if (normalized.length >= 2) {
    for (const tk of toolkits) {
      const name = (tk.name || '').toLowerCase();
      const slug = (tk.slug || '').toLowerCase();
      if (
        slug.startsWith(collapsed) ||
        name.startsWith(normalized) ||
        name.split(/[^a-z0-9]+/).some((word) => word && word.startsWith(collapsed))
      ) {
        add(tk, 2);
      }
    }
  }

  // Tier 3 — substring, reasonably specific input only so one letter never
  // matches half the catalog. Forward direction (query inside name/slug) is
  // raw contains — "drive" must find OneDrive. Reverse direction (name inside
  // the query) is WORD-BOUNDARY: "inbox" must never surface Box (the
  // 2026-08-11 regression, generalized).
  if (normalized.length >= 3 && normalized.length <= 30) {
    for (const tk of toolkits) {
      const name = (tk.name || '').toLowerCase();
      const slug = (tk.slug || '').toLowerCase();
      if (
        name.includes(normalized) ||
        slug.includes(collapsed) ||
        (name.length >= 3 && wordBoundaryHit(normalized, name))
      ) {
        add(tk, 3);
      }
    }
  }

  // Tier 4 — typo rescue. Only for queries long enough that an edit distance
  // means "typo" rather than "different word".
  if (normalized.length >= 4) {
    const maxEdits = normalized.length <= 6 ? 1 : 2;
    for (const tk of toolkits) {
      const key = (tk.slug || '').toLowerCase();
      if (scored.has(key)) continue; // already matched a better tier
      const name = (tk.name || '').toLowerCase();
      if (
        editDistanceWithin(collapsed, key, maxEdits) ||
        (name && editDistanceWithin(normalized, name, maxEdits))
      ) {
        add(tk, 4);
      }
    }
  }

  // Tiers 3–5, broad only: the name's words in any order ("calendar google",
  // "gogle calender"), then what the app does. 3+ letters, like the
  // substring tier, so a letter or two never matches half the catalog.
  if (opts?.broad && normalized.length >= 3) {
    const queryWords = searchWords(normalized);
    for (const tk of toolkits) {
      if (scored.has((tk.slug || '').toLowerCase())) continue;
      const tier = wordsMatchTier(queryWords, nameWordsOf(tk));
      if (tier !== null) add(tk, tier);
      else if (relatedHit(queryWords, relatedTextOf(tk))) add(tk, 5);
    }
  }

  const ranked = Array.from(scored.values()).sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    const aClick = connectLaneFor(a.tk) === 'oauth' ? 0 : 1;
    const bClick = connectLaneFor(b.tk) === 'oauth' ? 0 : 1;
    if (aClick !== bClick) return aClick - bClick;
    const curated = sortByLikelihood(a.tk, b.tk);
    if (curated !== 0) return curated;
    return (b.tk.meta?.tools_count || 0) - (a.tk.meta?.tools_count || 0);
  });

  return opts?.limit ? ranked.slice(0, opts.limit) : ranked;
}

/**
 * Chat-resolver view of the same ranking (connect-app cards): keeps the old
 * semantics — exact/synonym hits are authoritative and suppress the broader
 * tiers; prefix/substring/fuzzy only surface when nothing better matched.
 * Ordering preserves the historical card sort: genuinely available one-click
 * OAuth first, then tier, then tool count.
 */
export function searchToolkitsForResolver(
  query: string,
  toolkits: ComposioToolkit[],
  limit: number = 3
): ScoredToolkit[] {
  const ranked = searchToolkits(query, toolkits);
  if (ranked.length === 0) return [];
  const best = ranked[0].score;
  const gated = best <= 1 ? ranked.filter((r) => r.score <= 1) : ranked;
  return gated
    .slice() // stable copy before re-sorting with the card comparator
    .sort((a, b) => {
      const aOneClick = laneIsDirect(connectLaneFor(a.tk)) ? 0 : 1;
      const bOneClick = laneIsDirect(connectLaneFor(b.tk)) ? 0 : 1;
      if (aOneClick !== bOneClick) return aOneClick - bOneClick;
      if (a.score !== b.score) return a.score - b.score;
      return (b.tk.meta?.tools_count || 0) - (a.tk.meta?.tools_count || 0);
    })
    .slice(0, limit);
}
